import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { routeService } from '../../../services/routeService';
import './AdminRoutes.css';

// Компонент списка маршрутов для панели администратора
const AdminRoutesList = () => {
  const navigate = useNavigate();

  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');      // Поисковый запрос
  const [deletingId, setDeletingId] = useState(null);
  
  // Вспомогательная функция: обрезает длинный текст до maxLength символов
  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Загрузка маршрута при монтировании компонента
  useEffect(() => {
    fetchRoutes();
  }, []);

  // Функция загрузки маршрута из api
  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const routeData = await routeService.getAllRoutes();
      setRoutes(Array.isArray(routeData) ? routeData : []);
      setError(null);
    } catch (error) {
      console.error("Ошибка загрузки маршрутов:", error)
      setError('Не удалось загрузить список маршрутов');
    } finally {
      setLoading(false);
    }
  }

  // Функция удаления маршрута с подтверждением
  const handleDeleteRoute = async (id, name) => {
    console.log('НАЧАЛО УДАЛЕНИЯ');
    console.log('ID маршрута для удаления:', id);
    console.log('Название маршрута:', name);

    // Подтверждение через нативный диалог браузера
    const isConfirmed = window.confirm(
      `Вы уверены, что хотите удалить локацию "${name}"?\n\n` +
      `Это действие нельзя отменить.`
    );

    if (!isConfirmed) {
      console.log('Удаление отменено пользователем');
      return;
    }

    try {
      setDeletingId(id) // Блокировка кнопки удаления для этого маршрута
      console.log('Вызов routeService.deleteRoute с ID:', id);
      const result = await routeService.deleteRoute(id);
      console.log('Результат удаления:', result);

      // Удаление маршрута из состояния без повторного запроса
      setRoutes(prevRoutes => {
        const newRoutes = prevRoutes.filter(route => route.id !== id);
        console.log('Обновленный список маршрутов:', newRoutes);
        return newRoutes;
      });
      alert('Маршрут успешно удален');
    } catch (error) {
      console.error('Ошибка удаления:', error);
      console.error('Статус ошибки:', error.status);
      console.error('Сообщение ошибки:', error.message);
      alert(`Не удалось удалить маршрут: ${error.message}`);     
    } finally {
      setDeletingId(null); // Разблокируем кнопки
      console.log('УДАЛЕНИЕ ЗАВЕРШЕНО');
    }
  };

  // Фильтрация маршрутов по поисковому запросу (по названию или описанию)
  const filteredRoutes = routes.filter(route =>
    route.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Получение url первого или главного фото для миниатюры
  const getPreviewPhotoUrl = (route) => {
    if (route.photos && route.photos.length > 0) {
      return route.photos[0].url;
    }
    if (route.mainPhotoId && route.photos) {
      const mainPhoto = route.photos.find(p => p.id === route.mainPhotoId);
      if (mainPhoto) return mainPhoto.url;
    }
    return null;
  };

  // Состояние загрузки
  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Загрузка маршрута...</p>
      </div>
    );
  }

  // Состояние ошибки
  if (error) {
    return (
      <div className="admin-error">
        <span className="error-icon">⚠️</span>
        <p>{error}</p>
        <button onClick={fetchRoutes} className="btn btn-primary">
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div className="admin-routes-list">
      {/* Верхняя панель с заголовком и кнопкой создания */}
      <div className="admin-routes-list_header">
        <h2>Управление маршрутами</h2>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/admin/locations/routes/add')}
        >
          + Создать маршрут
        </button>
      </div>

      {/* Поле поиска с кнопкой очистки */}
      <div className="admin-routes-list_search">
        <input
          type="text"
          placeholder="Поиск по названию или описанию..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button 
            className="search-clear"
            onClick={() => setSearchTerm('')}
          >
            ✕
          </button>
        )}
      </div>

      {/* Если ничего не найдено*/}
      {filteredRoutes.length === 0 ? (
        <div className="admin-routes-list_empty">
          {searchTerm ? (
            <>
              <span className="empty-icon">🔍</span>
              <p>По запросу "{searchTerm}" ничего не найдено</p>
              <button 
                className="btn btn-secondary"
                onClick={() => setSearchTerm('')}
              >
                Сбросить поиск
              </button>
            </>
          ) : (
            <>
              <span className="empty-icon">📍</span>
              <p>Маршруты не найдены</p>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/admin/locations/routes/add')}
              >
                Создать первый маршрут
              </button>
            </>
          )}
        </div>
      ) : (
        // Таблица с локациями
        <div className="admin-routes-list_table-container">
          <table className="admin-routes-list_table">
            <thread>
              <tr>
                <th>ID</th>
                <th>Фото</th>
                <th>Название</th>
                <th>Описание</th>
                <th>Точек</th>      {/* Количество точек в маршруте */}
                <th>Фото</th>       {/* Количество фото */}
                <th>Действия</th>
              </tr>
            </thread>
            <tbody>
              {filteredRoutes.map(route => {
                const previewUrl = getPreviewPhotoUrl(route);
                const pointsCount = route.points?.length || 0;
                const photosCount = route.photos?.length || 0;

                return (
                  <tr key={route.id}>
                    <td className="id-cell">{route.id}</td>

                    {/* Миниатюра фото */}
                    <td className="photo-cell">
                      {previewUrl ? (
                        <img 
                          src={previewUrl}
                          alt={route.name}
                          className="route-thub"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                      ) : (
                        <div className="route-thumb placeholder">
                          📷
                        </div>
                      )}
                    </td>

                    <td className="name-cell">
                      <span className="route-name">{route.name}</span>
                    </td>

                    <td className="description-cell">
                      {truncateText(route.description, 60)}
                    </td>

                    <td className="stats-cell">
                      <span className="stat-badge photos">
                        📍 {pointsCount}
                      </span>
                    </td>

                    <td className="stats-cell">
                      <span className="stat-badge photos">
                        📷 {photosCount}
                      </span>
                    </td>

                    {/* Кнопки действий */}
                    <td className="actions-cell">
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => navigate(`/admin/locations/routes/edit/${route.id}`)}
                        title="Редактировать"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDeleteRoute(route.id, route.name)}
                        disabled={deletingId === route.id} // Блокировка во время удаления
                        title="Удалить"
                      >
                        {deletingId === route.id ? '⏳' : '🗑️'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Подвал с информацией о количестве */}
      {filteredRoutes.length > 0 && (
        <div className="admin-routes-list_footer">
          <span className="stats-info">
            Всего: {filteredRoutes.length} из {routes.length} маршрутов
          </span>
        </div>
      )}
    </div>
  );
}

export default AdminRoutesList;